/**
 * React Query client configuration
 * Provides default settings for data fetching and caching
 */

import { QueryClient } from '@tanstack/react-query';

import { ApiClientError, UnauthorizedError } from '../services/api';

// ============================================================================
// DEFAULT QUERY OPTIONS
// ============================================================================

const DEFAULT_STALE_TIME = 5 * 60 * 1000; // 5 minutes
const DEFAULT_CACHE_TIME = 10 * 60 * 1000; // 10 minutes
const DEFAULT_RETRY_COUNT = 1;
const DEFAULT_RETRY_DELAY = 1000; // 1 second

// ============================================================================
// ERROR HANDLER
// ============================================================================

function handleQueryError(error: unknown): void {
  if (error instanceof UnauthorizedError) {
    // User is not authenticated - redirect to login
    console.error('Unauthorized error:', error.message);
    // The AuthContext will handle the redirect
    return;
  }

  if (error instanceof ApiClientError) {
    console.error(`API Error [${error.code}]:`, error.message, error.details);
    return;
  }

  if (error instanceof Error) {
    console.error('Query error:', error.message);
    return;
  }

  console.error('Unknown query error:', error);
}

// ============================================================================
// RETRY LOGIC
// ============================================================================

function shouldRetry(failureCount: number, error: unknown): boolean {
  // Don't retry on client errors (4xx)
  if (error instanceof ApiClientError) {
    const statusCode = error.statusCode;
    if (statusCode >= 400 && statusCode < 500) {
      return false;
    }
  }

  // Retry up to the default count for server errors
  return failureCount < DEFAULT_RETRY_COUNT;
}

// ============================================================================
// QUERY CLIENT INSTANCE
// ============================================================================

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Time before data is considered stale
      staleTime: DEFAULT_STALE_TIME,

      // Time before inactive queries are garbage collected
      gcTime: DEFAULT_CACHE_TIME,

      // Retry failed requests
      retry: shouldRetry,

      // Delay between retries
      retryDelay: DEFAULT_RETRY_DELAY,

      // Don't refetch on window focus by default (can enable per-query)
      refetchOnWindowFocus: false,

      // Don't refetch on reconnect by default
      refetchOnReconnect: false,

      // Don't refetch on mount by default
      refetchOnMount: false,

      // Throw errors to error boundaries
      throwOnError: false,
    },
    mutations: {
      // Retry failed mutations once
      retry: shouldRetry,

      // Delay between retries
      retryDelay: DEFAULT_RETRY_DELAY,

      // Error handler for mutations
      onError: handleQueryError,

      // Throw errors to error boundaries
      throwOnError: false,
    },
  },
});

// ============================================================================
// QUERY KEY FACTORIES
// ============================================================================

/**
 * Query key factory for teams
 */
export const teamsKeys = {
  all: ['teams'] as const,
  lists: () => [...teamsKeys.all, 'list'] as const,
  list: () => [...teamsKeys.lists()] as const,
  details: () => [...teamsKeys.all, 'detail'] as const,
  detail: (id: string) => [...teamsKeys.details(), id] as const,
};

/**
 * Query key factory for projects
 */
export const projectsKeys = {
  all: ['projects'] as const,
  lists: () => [...projectsKeys.all, 'list'] as const,
  list: (teamId?: string) => [...projectsKeys.lists(), { teamId }] as const,
  deleted: (teamId?: string) => [...projectsKeys.all, 'deleted', { teamId }] as const,
  details: () => [...projectsKeys.all, 'detail'] as const,
  detail: (id: string) => [...projectsKeys.details(), id] as const,
  stats: (id: string) => [...projectsKeys.detail(id), 'stats'] as const,
};

/**
 * Query key factory for creatives
 */
export const creativesKeys = {
  all: ['creatives'] as const,
  lists: () => [...creativesKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...creativesKeys.lists(), filters] as const,
  details: () => [...creativesKeys.all, 'detail'] as const,
  detail: (id: string) => [...creativesKeys.details(), id] as const,
  performanceContext: (id: string, scope?: Record<string, unknown>) =>
    [...creativesKeys.detail(id), 'performanceContext', scope] as const,
};

/**
 * Query key factory for analyses
 */
export const analysesKeys = {
  all: ['analyses'] as const,
  lists: () => [...analysesKeys.all, 'list'] as const,
  list: (creativeId?: string) => [...analysesKeys.lists(), { creativeId }] as const,
  details: () => [...analysesKeys.all, 'detail'] as const,
  detail: (id: string) => [...analysesKeys.details(), id] as const,
  status: (id: string) => [...analysesKeys.detail(id), 'status'] as const,
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Invalidate all queries for a specific resource
 */
export async function invalidateResource(resource: 'teams' | 'projects' | 'creatives' | 'analyses'): Promise<void> {
  const keys = {
    teams: teamsKeys.all,
    projects: projectsKeys.all,
    creatives: creativesKeys.all,
    analyses: analysesKeys.all,
  };

  await queryClient.invalidateQueries({ queryKey: keys[resource] });
}

/**
 * Clear all cached data
 */
export async function clearCache(): Promise<void> {
  await queryClient.clear();
}

/**
 * Prefetch a query
 */
export async function prefetchQuery<T>(
  queryKey: readonly unknown[],
  queryFn: () => Promise<T>
): Promise<void> {
  await queryClient.prefetchQuery({
    queryKey,
    queryFn,
  });
}
